import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Mic, Square, CheckCircle2, Info } from "lucide-react";
import { api, fileUrl } from "../../lib/api";
import { toast } from "sonner";

const CANDIDATE_MIME_TYPES = [
    "audio/ogg;codecs=opus",
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/mp4",
];

function pickMimeType() {
    if (typeof MediaRecorder === "undefined") return null;
    for (const t of CANDIDATE_MIME_TYPES) {
        if (MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(t)) return t;
    }
    return "";
}

export default function EvaluacionOralPractica() {
    const { assessmentId } = useParams();
    const navigate = useNavigate();

    const [assessment, setAssessment] = useState(null);
    const [loading, setLoading] = useState(true);
    const [session, setSession] = useState(null);
    const [starting, setStarting] = useState(false);
    const [recording, setRecording] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [secondsLeft, setSecondsLeft] = useState(0);
    const [finished, setFinished] = useState(false);

    const mediaRecorderRef = useRef(null);
    const chunksRef = useRef([]);
    const streamRef = useRef(null);
    const intervalRef = useRef(null);
    const recordStartRef = useRef(0);
    const mimeTypeRef = useRef("");

    useEffect(() => {
        api.get(`/oral-assessments/${assessmentId}`)
            .then((r) => setAssessment(r.data))
            .catch((e) => toast.error(e.response?.data?.detail || "Error cargando la evaluación"))
            .finally(() => setLoading(false));
        return () => {
            clearInterval(intervalRef.current);
            streamRef.current?.getTracks().forEach((t) => t.stop());
        };
    }, [assessmentId]);

    async function startSession() {
        setStarting(true);
        try {
            const { data } = await api.post(`/oral-assessments/${assessmentId}/sessions`);
            setSession(data);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Error iniciando la práctica");
        } finally {
            setStarting(false);
        }
    }

    async function startRecording() {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            streamRef.current = stream;
            const mimeType = pickMimeType();
            mimeTypeRef.current = mimeType;
            const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
            mediaRecorderRef.current = recorder;
            chunksRef.current = [];

            recorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
            recorder.onstop = () => { streamRef.current?.getTracks().forEach((t) => t.stop()); };
            recorder.start();
            recordStartRef.current = Date.now();
            setRecording(true);

            const limite = session.tiempo_por_respuesta_seg || 120;
            setSecondsLeft(limite);
            intervalRef.current = setInterval(() => {
                setSecondsLeft((prev) => {
                    if (prev <= 1) {
                        clearInterval(intervalRef.current);
                        stopRecording();
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        } catch (e) {
            toast.error("No se pudo acceder al micrófono. Revisa los permisos del navegador.");
        }
    }

    function stopRecording() {
        clearInterval(intervalRef.current);
        const recorder = mediaRecorderRef.current;
        if (!recorder || recorder.state === "inactive") return;
        setRecording(false);
        const duracionSeg = Math.max(1, Math.round((Date.now() - recordStartRef.current) / 1000));
        recorder.onstop = async () => {
            streamRef.current?.getTracks().forEach((t) => t.stop());
            const blob = new Blob(chunksRef.current, { type: mimeTypeRef.current || "audio/webm" });
            await submitAnswer(blob, duracionSeg);
        };
        recorder.stop();
    }

    async function submitAnswer(blob, duracionSeg) {
        setSubmitting(true);
        try {
            const fd = new FormData();
            const ext = (mimeTypeRef.current || "audio/webm").split("/")[1]?.split(";")[0] || "webm";
            fd.append("file", blob, `respuesta.${ext}`);
            fd.append("duracion_seg", String(duracionSeg));
            const { data } = await api.post(`/oral-assessments/sessions/${session.id}/answer`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            if (data.closed) {
                setFinished(true);
                toast.success("Práctica terminada — revisa tu reporte");
            } else {
                setSession((prev) => ({
                    ...prev,
                    rounds: [...(prev.rounds || []), { round_num: (prev.rounds?.length || 0) + 1, pregunta: data.next_question, imagen_file_id: data.next_imagen_file_id || null }],
                }));
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || "Error enviando tu respuesta");
        } finally {
            setSubmitting(false);
        }
    }

    if (loading) return <div className="p-10 text-edu-muted">Cargando…</div>;
    if (!assessment) return <div className="p-10 text-edu-muted">No se pudo cargar.</div>;

    if (finished) {
        return (
            <div className="p-6 md:p-10 fade-in min-h-screen max-w-lg mx-auto text-center">
                <div className="edu-card p-10">
                    <CheckCircle2 size={40} className="text-emerald-600 mx-auto mb-3" />
                    <h1 className="text-xl font-semibold mb-2">¡Práctica completa!</h1>
                    <p className="text-sm text-edu-muted mb-6">Ya tienes tu reporte con la transcripción y los comentarios por criterio.</p>
                    <button onClick={() => navigate(`/alumno/evaluacion-oral/sesion/${session.id}`)} className="edu-btn-primary px-5 py-2.5 text-sm">
                        Ver mi reporte
                    </button>
                </div>
            </div>
        );
    }

    if (!session) {
        return (
            <div className="p-6 md:p-10 fade-in min-h-screen max-w-lg mx-auto">
                <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-sm text-edu-body hover:text-edu-navy mb-6">
                    <ArrowLeft size={16} /> Volver
                </button>
                <div className="edu-card p-8 text-center">
                    <Mic size={32} className="text-edu-navy mx-auto mb-3" />
                    <h1 className="text-xl font-semibold mb-1">{assessment.titulo}</h1>
                    <p className="text-sm text-edu-muted mb-1">{assessment.duracion_total_min} minutos en total · {assessment.tiempo_por_respuesta_seg} segundos por respuesta</p>
                    <p className="text-xs text-edu-muted mb-6 inline-flex items-center gap-1.5"><Info size={12} /> Necesitarás dar permiso de micrófono al navegador.</p>
                    <button onClick={startSession} disabled={starting} className="edu-btn-primary px-6 py-3 text-sm disabled:opacity-50">
                        {starting ? "Preparando…" : "Comenzar práctica"}
                    </button>
                </div>
            </div>
        );
    }

    const currentRound = session.rounds?.[session.rounds.length - 1];

    return (
        <div className="p-6 md:p-10 fade-in min-h-screen max-w-lg mx-auto">
            <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-sm text-edu-body hover:text-edu-navy mb-6">
                <ArrowLeft size={16} /> Salir de la práctica
            </button>

            <div className="edu-card p-8 text-center">
                <p className="text-xs text-edu-muted uppercase tracking-widest font-semibold mb-4">Pregunta {currentRound?.round_num}</p>
                {currentRound?.imagen_file_id && (
                    <img
                        src={fileUrl(currentRound.imagen_file_id)}
                        alt="Imagen de la pregunta"
                        className="max-h-64 mx-auto rounded-xl border border-edu mb-6 object-contain"
                    />
                )}
                <p className="text-lg font-medium mb-8">{currentRound?.pregunta}</p>

                {recording ? (
                    <>
                        <div className="text-3xl font-semibold text-edu-navy mb-4">{secondsLeft}s</div>
                        <button onClick={stopRecording} className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-red-600 text-white text-sm">
                            <Square size={16} /> Terminar respuesta
                        </button>
                    </>
                ) : (
                    <button onClick={startRecording} disabled={submitting} className="edu-btn-primary px-6 py-3 text-sm inline-flex items-center gap-2 disabled:opacity-50">
                        <Mic size={16} /> {submitting ? "Enviando…" : "Grabar respuesta"}
                    </button>
                )}
            </div>
        </div>
    );
}
